import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TaskDataService } from '../../../../shared/services/task-data.service';
import * as FandCActions from './fandc-generator.actions';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';
import { Store } from '@ngrx/store';
import notify from 'devextreme/ui/notify';
import { getNotificationTimeout } from '../../../../types/resource';
import {
  selectedLoanNumber,
  selectedFrequency,
  selectFilteredPrototypeTasks,
  selectMaturityDate,
  selectPrototypeTasks,
  selectReferenceDate,
  selectFrequencyOffsetValue,
  selectFirstDueDate,
  selectLoanDetail,
  selectGeneratedTasks,
  selectSelectedFormatOption,
  selectSelectedReportEntity,
  selectSelectedEntityName,
  selectReportEntityOptions,
  selectSelectedTemplateType,
  selectTemplates,
  selectRootTemplate,
  selectSelectedPrototypeTasks,
  selectTaskId,
  selectTaskGenerationMetadataId,
  selectSelectedLoanDetail,
} from './fandc-generator.selectors';
import { IGeneratedTasksModel, ITaskRequest, TaskRequest } from '../../../../modules/tasks/model/types';
import { Router } from '@angular/router';
import { FrequencyOptionsService } from '../services/frequencyoptions.service';
import { template } from 'lodash';
import { format } from 'date-fns';

@Injectable()
export class FandCGeneratorEffects {
  private actions$ = inject(Actions);
  private taskDataService = inject(TaskDataService);
  private store = inject(Store);
  private router = inject(Router);
  private frequencyOptionsService = inject(FrequencyOptionsService);

  constructor() {}

  fetchTemplates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FandCActions.fetchTemplates),
      switchMap(({ criteria }) =>
        this.taskDataService.getTaskTemplates(false).pipe(
          map((response) => {
            // console.log('fetchTemplates response', response);
            const filteredData = Array.isArray(response[1])
              ? response[1].filter(
                  (item) =>
                    !criteria.referenceTypeName ||
                    (item.referenceType === criteria.referenceTypeName && item.templateType === criteria.templateName)
                )
              : [];
              // console.log('filteredData', filteredData);
            return FandCActions.fetchTemplatesSuccess({ data: filteredData });
          })
        )
      )
    )
  );

  fetchRootTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FandCActions.fetchRootTemplate),
      switchMap(({ templateName }) =>
        this.taskDataService.getTaskTemplatesByType(templateName).pipe(
          map((response) => {

            const filteredData = Array.isArray(response[1])
              ? response[1].filter(
                  (item) =>
                    item.templateType === templateName
                )
              : [];

            return FandCActions.fetchRootTemplateSuccess({ data: filteredData.length > 0 ? filteredData[0] : null });
          })
        )
      )
    )
  );  

  fetchTemplatesTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FandCActions.fetchTemplateTypes),
      switchMap(({ criteria }) =>
        this.taskDataService.getTaskTemplates(false).pipe(
          map((response) => ({
            referenceType: criteria.referenceTypeName,
            response: response,
          }))
        )
      ),
      map((result) => {
        if (result && result.response) {
          const filteredData = Array.isArray(result.response[1])
            ? result.response[1].filter(
                (item) =>
                  !result.referenceType ||
                  item.referenceType === result.referenceType
              )
            : [];
          return { data: filteredData };
        } else {
          return { data: [] };
        }
      }),
      map((result) => {
        if (result && Array.isArray(result.data)) {
          // Extract unique templateTypes and map to desired object shape
          const uniqueTemplateTypes = Array.from(
            new Set(result.data.map((item) => item.templateType))
          );
          return FandCActions.fetchTemplateTypesSuccess({
            data: uniqueTemplateTypes.map((templateType) => ({ templateType })),
          });
        }
        return FandCActions.fetchTemplateTypesSuccess({ data: [] });
      })
    )
  );

  fetchDefaultTasksForTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FandCActions.fetchDefaultTasksForTemplate),
      withLatestFrom(
        this.store.select(selectTemplates),
      ),
      switchMap(([ action, templates ]) =>
      {
        // Find the template that matches the criteria
        const matchingTemplate = templates.find(t => t.name === action.criteria.templateName);
        const templateId = matchingTemplate?.id;
      
        return this.taskDataService
          .getDefaultTasksForTemplate(templateId)
          .pipe(
            map((result) => {
              const returnedData = result[1];
              return FandCActions.fetchDefaultTasksForTemplateSuccess({
                data: returnedData,

              });
            })
          )
        }
      )
    )
  );

  setLoanNumber$ = createEffect(() =>
  this.actions$.pipe(
    ofType(FandCActions.setLoanNumber),
    map(({ selectedLoan }) => {
      // If selectedLoanNumber is an object, use selectedLoanNumber.id
      // If it's just an ID, use it directly
      const facilityId = selectedLoan?.id;
      return FandCActions.fetchLoanDetail({ facilityId });
    })
  )
);

  fetchLoans$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FandCActions.fetchLoans),
      switchMap(() =>
        this.taskDataService.getLoanNumbersWithDeal().pipe(
          map((response) => {
            const data = Array.isArray(response[1])
              ? response[1]
              : [];
            return FandCActions.fetchLoansSuccess({ data });
          })
        )
      )
    )
  );

    fetchReportEntityOptions$ = createEffect(() =>
      this.actions$.pipe(
        ofType(FandCActions.fetchReportEntityOptions),
        map(() => {
          const data = [
            { label: 'Borrower', value: 'borrowers' },
            { label: 'Guarantor', value: 'guarantors' },
            { label: 'Joint Venture', value: 'jointVentures' },
            { label: 'Lender', value: 'lenders' },
            { label: 'Loan Parties', value: 'loanParties' },
            { label: 'Master Tenant', value: 'masterTenants' },
            { label: 'Operating Lessee', value: 'operatingLessees' },
            { label: 'Property', value: 'properties' },
          ];
          return FandCActions.fetchReportEntityOptionsSuccess({ data });
        })
      )
    );

  fetchLoanDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FandCActions.fetchLoanDetail),
      switchMap(({ facilityId }) => {
        if (!facilityId) {
          return of(FandCActions.fetchLoanDetailSuccess({ data: null }));
        }
        return this.taskDataService.getLoanDetail(facilityId).pipe(
          map((response) => {
            return FandCActions.fetchLoanDetailSuccess({ data: response[1] });
          })
        );
      })
    )
  );

  generateTasks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FandCActions.generateTasks),
      withLatestFrom(
        this.store.select(selectSelectedPrototypeTasks),
        this.store.select(selectReferenceDate),
        this.store.select(selectMaturityDate),
        this.store.select(selectedLoanNumber),
        this.store.select(selectedFrequency),
        this.store.select(selectFirstDueDate),
        this.store.select(selectSelectedFormatOption),
        this.store.select(selectSelectedEntityName)
      ),
      switchMap(
        ([
          action,
          prototypeTasks,
          referenceDate,
          maturityDate,
          loanNumber,
          frequency,
          firstDueDate,
          selectedFormatOption,
          selectedEntityName,
        ]) => {
          // Group prototype tasks by firstDueDate and frequency
          const taskGroups: { [key: string]: any[] } = {};
          
          if (Array.isArray(prototypeTasks)) {
            prototypeTasks.forEach((task: any) => {
              // Create a key based on reference date and frequency and entity name
              const taskDueDate = task.referenceDate || referenceDate;
              const taskUntilDate = task.untilDate || maturityDate;
              const taskFrequency = task.frequency || frequency;
              const taskEntityName = task.entityName || selectedEntityName;
              const groupKey = `${taskDueDate}_${taskUntilDate}_${taskFrequency}_${taskEntityName}`;
              
              if (!taskGroups[groupKey]) {
                taskGroups[groupKey] = [];
              }
              
              taskGroups[groupKey].push(task);
            });
          }

          // console.log('taskGroups', taskGroups);

          // Create an array of IGeneratedTasksModel objects
          const generatedTasksModels: IGeneratedTasksModel[] = [];
          
          // Process each group of tasks
          Object.entries(taskGroups).forEach(([groupKey, groupTasks]) => {
            const [groupDueDate, groupFrequencyStr] = groupKey.split('_');
            
            // Map tasks for this group
            const mappedTasks = groupTasks.map((task: any) => {
              const taskData: any = {
                loanNumber: loanNumber,
              };
              
              if (selectedFormatOption != null && selectedFormatOption !== '') {
                taskData.format = selectedFormatOption;
              }
              
              if (task.notes != null && task.notes !== '') {
                taskData.notes = task.notes;
              }
              
              if (task.reference != null && task.reference !== '') {
                taskData.reference = task.reference;
              }

              if (task.reportEntity != null && task.reportEntity !== '') {
                taskData.reportEntity = task.reportEntity;
              }

              if (task.reportEntityLabel != null && task.reportEntityLabel !== '') {
                taskData.reportEntityLabel = task.reportEntityLabel;
              }
              
              if (task.entityName != null && task.entityName !== '') {
                taskData.entityName = task.entityName;
              }
                          
              // console.log('taskData', taskData);
              
              return {
                id: task.id,
                taskName: task.taskName,
                description: task.description,
                dueDate: task.dueDate,
                assignedTo: task.assignedTo,
                assignedUserName: task.assignedUserName,
                taskConfigurationId: task.taskConfigurationId,
                team: task.team,
                templateId: task.templateId,
                taskData: JSON.stringify(taskData),
              };
            });
            
            // Ensure we're using the correct frequency type by using the sample's frequency
            // This assumes all tasks in the group have the same frequency
            const firstTask = groupTasks[0];
            const groupFrequency = firstTask?.frequency || frequency;
            const tenor = this.frequencyOptionsService.getTenor(groupFrequency);
            const groupReferenceDate = firstTask?.referenceDate || referenceDate;
            const groupUntilDate = firstTask?.untilDate;

            // Create an IGeneratedTasksModel for this group
            const groupModel: IGeneratedTasksModel = {
              assignedUserName: null,
              approverGroup: null,
              secondApproverGroup: null,
              thirdApproverGroup: null,
              team: null,
              referenceTypeId: '00000000-0000-0000-0000-000000000000', // empty GUID
              tasks: mappedTasks,
              referenceType: null,
              referenceId: '00000000-0000-0000-0000-000000000000', // empty GUID
              reference: loanNumber,
              referenceName: loanNumber,
              referenceDate: groupReferenceDate,
              tenor: tenor,
              frequency: null,
              numberOfCycles: null,
              untilDate: groupUntilDate,
              dateRollConvention: 'Following',
              location: 'US-GA',
              workflowId: null,
              referenceDateIsFirstDueDate: true,
            };
            
            generatedTasksModels.push(groupModel);
          });

          // console.log('generatedTasksModels', generatedTasksModels);
          

          // Call the service with the array of models
          return this.taskDataService.getGeneratedTasks(generatedTasksModels).pipe(
            map((result) => {
              // console.log('result', result);
              const returnedData = result[1] ?? [];
              return FandCActions.fetchGeneratedTasksSuccess({
                tasks: returnedData,
              });
            })
          );
        }
      )
    )
  );

  saveGeneratedTasks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FandCActions.saveGeneratedTasks),
      withLatestFrom(
        this.store.select(selectGeneratedTasks)
      ),
      switchMap(([
          action,
          generatedTasks,
        ]) => {
        // console.log('generatedTasks', generatedTasks);
        if (!generatedTasks || generatedTasks.length === 0) {
          return of(FandCActions.saveGeneratedTasksSuccess({ data: [] }));
        }
        return this.taskDataService.postGeneratedTasks(generatedTasks).pipe(
          map((result) => {
            const returnedData = result[1] ?? [];
            // console.log('postGeneratedTasks result', returnedData);
            return FandCActions.saveGeneratedTasksSuccess({
              data: returnedData,
            });
          })
        );
      })
    )
  );

  saveGenerationTask = createEffect(() =>
    this.actions$.pipe(
      ofType(FandCActions.saveGenerationMetadataSuccess),
      withLatestFrom(
        this.store.select(selectTaskId),
        this.store.select(selectedLoanNumber),
        this.store.select(selectRootTemplate)
      ),
      switchMap(([
          metadata,
          selectTaskId,
          selectedLoanNumber,
          selectRootTemplate,
        ]) => {
          
        // Only proceed if selectTaskId is null
        if (selectTaskId !== null) {
          return of(FandCActions.saveGenerationTaskSuccess({ 
            data: [{ task: { taskId: selectTaskId } }] 
          }));
        }

        // console.log('saveGenerationTask metadata', metadata);

        const metadataId = metadata.metadata.id;
        let request: ITaskRequest = new TaskRequest(); 
        request.templateId = selectRootTemplate?.id || ''; // Assuming the first template is selected

        request.name = "Creation";
        request.referenceName = selectedLoanNumber;
        request.taskGenerationMetadataId = metadataId;
        request.referenceType = 'Facility';

        let formData : FormData = new FormData()
        formData.append('templateId', request.templateId);
        formData.append('taskGenerationMetadataId', request.taskGenerationMetadataId);
        formData.append('name', request.name);
        formData.append('referenceName', request.referenceName);
        formData.append('requestJsonData', JSON.stringify({ loanNumber: selectedLoanNumber }));
        formData.append('referenceType', request.referenceType);

        return this.taskDataService.insertTaskAsFormData(formData).pipe(
          map((result) => {
            const returnedData = result[1].data ?? [];
            // console.log('saveGenerationTask result', returnedData);
            return FandCActions.saveGenerationTaskSuccess({
              data: returnedData,
            });
          }),
          // Handle any errors that occur during the service call
          // and ensure we still dispatch a success action to turn off the loading indicator
          catchError(error => {
            console.error('Error saving generation task', error);
            // Display error notification
            notify('Error saving generation task: ' + error.message, 'error', getNotificationTimeout());
            // Return empty data but still consider the operation "complete" to hide the loading indicator
            return of(FandCActions.saveGenerationTaskSuccess({
              data: []
            }));
          })
        );
      })
    )
  );

  saveGenerationMetadata = createEffect(() =>
    this.actions$.pipe(
      ofType(FandCActions.saveGenerationMetadata),
      withLatestFrom(
        this.store.select(selectSelectedLoanDetail),
        this.store.select(selectSelectedPrototypeTasks),
        this.store.select(selectTaskGenerationMetadataId)
      ),
      switchMap(([
          action,
          selectedLoanDetail,
          selectSelectedPrototypeTasks,
          selectTaskGenerationMetadataId
        ]) => {

          // console.log('selectedLoanDetail', selectedLoanDetail);
          const metadata = {
            id: selectTaskGenerationMetadataId || null, // Use existing ID or null for new metadata
            generationData: JSON.stringify({
              loanDetail: selectedLoanDetail,
              prototypeTasks: selectSelectedPrototypeTasks
            })
          };

        return this.taskDataService.postGenerateTaskMetadata(metadata).pipe(
          map((result) => {
            const returnedMetadata = result[1] ?? [];
            // console.log('postGenerateTaskMetadata result', returnedMetadata);
            return FandCActions.saveGenerationMetadataSuccess({
              metadata: returnedMetadata,
            });
          }),
          // Handle any errors during metadata saving
          catchError(error => {
            console.error('Error saving metadata:', error);
            // Display error notification
            notify('Error saving metadata: ' + error.message, 'error', getNotificationTimeout());
            // Return empty metadata but still proceed to next step to ensure the loading indicator is cleared
            return of(FandCActions.saveGenerationMetadataSuccess({
              metadata: { id: selectTaskGenerationMetadataId || 'error' }
            }));
          })
        );
      })
    )
  );
  
  fetchGenerationMetadata = createEffect(() =>
    this.actions$.pipe(
      ofType(FandCActions.fetchGenerationMetadata),
      switchMap((action) => {

        return this.taskDataService.getGenerateTaskMetadata(action.metadataId).pipe(
          map((result) => {
            const returnedMetadata = result[1] ?? [];
            // console.log('getGenerateTaskMetadata result', returnedMetadata);
            // Parse the metadata JSON string
            const returnedData = result[1] ?? {};
            if (returnedData && returnedData.generationData) {
              try {
              
              this.store.dispatch(
              FandCActions.setTaskGenerationMetadataId({ taskGenerationMetadataId: returnedData.id })
              );                

              const parsedData = JSON.parse(returnedData.generationData);
              
              // Dispatch setLoanNumber action if loanNumber exists
              if (parsedData.loanDetail) {
                this.store.dispatch(
                FandCActions.setLoanDetail({ loanDetail: parsedData.loanDetail })
                );
              }
              
              // Dispatch setSelectedPrototypeTasks action if prototypeTasks exists
              if (parsedData.prototypeTasks) {
                this.store.dispatch(
                FandCActions.setSelectedPrototypeTasks({ 
                  selectedPrototypeTasks: parsedData.prototypeTasks 
                })
                );
              }
              } catch (error) {
              console.error('Error parsing generationData:', error);
              }
            }
            
            return FandCActions.fetchGenerationMetadataSuccess({
              metadata: returnedMetadata,
            });
          })
        );
      })
    )
  );    

  navigateOnPostSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FandCActions.saveGeneratedTasksSuccess, FandCActions.saveGenerationTaskSuccess),
        tap((result) => {
          // console.log('result', result);

          const taskIds = Array.isArray(result?.data)
            ? result.data.map((item: any) => item.task?.taskId)
            : [];

          // Filter taskIds to unique prefixes (e.g., FAC-134 from FAC-134-1, FAC-134-2)
          const uniquePrefixes = Array.from(new Set(
            taskIds
              .filter((id: string) => id && typeof id === 'string')
              .map((id: string) => {
                const match = id.match(/^([A-Z]+-\d+)/);
                return match ? match[1] : id;
              })
          ));

          const event = {
            taskIds:uniquePrefixes,
            filterTypeMessage: 'Filtered by created / updated task',
          };
          this.router
            .navigateByUrl('/', { skipLocationChange: true })
            .then(() => {
              this.router.navigate(['/tasks'], { state: event });
            });
        })
      ),
    { dispatch: false } // Important: this effect does not dispatch a new action
  );

    setFrequencyOptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FandCActions.setFrequency),
      map(({ selectedFrequency }) => {
        const options = this.frequencyOptionsService.getFrequencyOffsetOptions(selectedFrequency);
        // console.log('Frequency Offset Options:', options);
        return FandCActions.setFrequencyOffsetOptions({ options });
      })
    )
  );

  fetchFrequencyOptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FandCActions.fetchFrequencyOptions),
      map(() => {
        const options = this.frequencyOptionsService.getFrequencyOptions();
        // console.log('Frequency Options:', options);
        return FandCActions.setFrequencyOptions({ options });
      })
    )
  );

  fetchFormatOptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FandCActions.fetchFormatOptions),
      map(() => {
        const options = [
          "Approved by Agent",
          "Audited",
          "Certified",
          "Certified, Audited",
          "Certified, Unaudited",
          "N/A",
          "Not Specified",
          "Satisfactory to Lender(s)",
          "Unaudited"
        ];
        return FandCActions.setFormatOptions({ options });
      })
    )
  );  

setEntityNameOptions$ = createEffect(() =>
  this.actions$.pipe(
    ofType(FandCActions.setReportEntity),
    withLatestFrom(this.store.select(selectLoanDetail)),
    map(([action, loanDetail]) => {
      const selectedReportEntity = action.selectedReportEntity;
      // console.log('Selected Report Entity:', selectedReportEntity);
      // console.log('Loan Detail:', loanDetail);
      const facilityDetail = loanDetail.facilityDetail || {};
      let options: { label: string; value: string }[] = [];
      if (loanDetail && selectedReportEntity && facilityDetail[selectedReportEntity]) {
        options = (facilityDetail[selectedReportEntity] || []).map((item: any) => ({
          label: item.name,
          value: item.id
        }));
      }
      // console.log('Entity Name Options:', options);
      return FandCActions.setEntityNameOptions({ options });
    })
  )
);  

setFirstDueDate$ = createEffect(() =>
  this.actions$.pipe(
    ofType(FandCActions.setReferenceDate, FandCActions.setFrequencyOffsetValue),
    withLatestFrom(
      this.store.select(selectReferenceDate),
      this.store.select(selectedFrequency),
      this.store.select(selectFrequencyOffsetValue)
    ),
    map(([action, referenceDate, frequency, frequencyOffsetValue]) => {
      // console.log(referenceDate, frequency, frequencyOffsetValue);
      const firstDueDate = referenceDate && frequency && frequencyOffsetValue
        ? this.frequencyOptionsService.getFirstDueDate(
            frequency,
            frequencyOffsetValue,
            referenceDate
          )
        : null;
      // console.log('First Due Date:', firstDueDate);
      return FandCActions.setFirstDueDate({ firstDueDate });
    })
  )
);
}
